import React from 'react';
import {
  ShieldCheck,
  Zap,
  Star,
  Battery,
  Wifi,
  Eye,
  Heart,
  Flame,
  Sparkles,
  Gift,
  Award,
  Camera,
  Clock,
  Leaf,
  Sun,
  Moon,
  Check,
  Lock,
  Tag,
  BadgeCheck,
  Crown,
  Rocket,
} from 'lucide-react';

// Curated list of icons admins can pick for their badge.
export const BADGE_ICONS = {
  shield: ShieldCheck,
  zap: Zap,
  star: Star,
  battery: Battery,
  wifi: Wifi,
  eye: Eye,
  heart: Heart,
  flame: Flame,
  sparkles: Sparkles,
  gift: Gift,
  award: Award,
  camera: Camera,
  clock: Clock,
  leaf: Leaf,
  sun: Sun,
  moon: Moon,
  check: Check,
  lock: Lock,
  tag: Tag,
  badge: BadgeCheck,
  crown: Crown,
  rocket: Rocket,
};

// Preset color palettes — each entry yields bg / border / icon / text classes.
export const BADGE_COLORS = {
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  icon: 'text-violet-500',  text: 'text-violet-700'  },
  cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',    icon: 'text-cyan-500',    text: 'text-cyan-700'    },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'text-blue-500',    text: 'text-blue-700'    },
  green:   { bg: 'bg-green-50',   border: 'border-green-200',   icon: 'text-green-500',   text: 'text-green-700'   },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', text: 'text-emerald-700' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   icon: 'text-amber-500',   text: 'text-amber-700'   },
  orange:  { bg: 'bg-orange-50',  border: 'border-orange-200',  icon: 'text-orange-500',  text: 'text-orange-700'  },
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     icon: 'text-red-500',     text: 'text-red-700'     },
  pink:    { bg: 'bg-pink-50',    border: 'border-pink-200',    icon: 'text-pink-500',    text: 'text-pink-700'    },
  slate:   { bg: 'bg-slate-100',  border: 'border-slate-300',   icon: 'text-slate-600',   text: 'text-slate-700'   },
  black:   { bg: 'bg-gray-900',   border: 'border-gray-900',    icon: 'text-white',       text: 'text-white'       },
};

export const BADGE_COLOR_LABELS = {
  violet: 'Violet',
  cyan: 'Cyan',
  blue: 'Bleu',
  green: 'Vert',
  emerald: 'Émeraude',
  amber: 'Ambre',
  orange: 'Orange',
  red: 'Rouge',
  pink: 'Rose',
  slate: 'Gris',
  black: 'Noir',
};

/**
 * Renders a product custom badge.
 * props.badge: { enabled, text, color, icon }
 * props.size: 'sm' (catalog cards) | 'md' (product detail)
 */
export default function ProductBadge({ badge, size = 'sm' }) {
  if (!badge || !badge.enabled || !badge.text) return null;
  const palette = BADGE_COLORS[badge.color] || BADGE_COLORS.violet;
  const Icon = BADGE_ICONS[badge.icon] || ShieldCheck;

  if (size === 'md') {
    return (
      <div
        className={`inline-flex items-center gap-1.5 ${palette.bg} border ${palette.border} rounded-full px-3 py-1.5`}
        data-testid="product-custom-badge"
      >
        <Icon size={14} className={`${palette.icon} shrink-0`} />
        <span className={`text-xs ${palette.text} font-semibold`}>{badge.text}</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-1 mt-2 ${palette.bg} border ${palette.border} rounded-lg px-2 py-1`}
      data-testid="product-custom-badge"
    >
      <Icon size={11} className={`${palette.icon} shrink-0`} />
      <span className={`text-[10px] ${palette.text} font-medium leading-tight`}>{badge.text}</span>
    </div>
  );
}
